import React, { FC, useContext, useEffect, useRef, useState } from "react";
import useSWR from "swr";

import { AppContext } from "../../../app-context";

import { getAccounts } from "../../apis/get-accounts";
import AddProfileModal, { Steps } from "../../components/add-profile-modal/add-profile-modal";
import { useLayout } from "../../hooks/use-layout";
import { useQuery } from "../../hooks/use-query";
import { useReturnUrl } from "../../hooks/use-return-url";
import { AccountV1 } from "../../models/account-v1";

import {
  Button,
  ButtonVariant,
  FaIcon,
  FaIcons,
  LayoutGrid,
  Notification,
  Section,
  Theme
} from "@in/component-library";
import { useNavigate } from "react-router-dom";
import AccountCard from "../../components/account-card/account-card";
import Page from "../../components/page/page";

const ChooseAccount: FC = () => {
  const { returnToPreviousUrl } = useReturnUrl();
  const query = useQuery();
  const disableSearch = query.get("disableSearch") === "true";
  const disablePersonal = query.get("disablePersonal") === "true";

  const { selectAccount } = useContext(AppContext);
  const { translations, layout } = useLayout();
  const { data, error } = useSWR(
    `accounts?enablePersonal=${!disablePersonal}&enableExternal=${!disableSearch}`,
    getAccounts
  );

  const [accounts, setAccounts] = useState<AccountV1[]>();
  const [initialStep, setInitialStep] = useState<Steps>(Steps.AccountSearch);
  const [showAddProfileModal, setShowAddProfileModal] = useState<boolean>(false);

  const addCompanyRef = useRef<HTMLButtonElement>(undefined);
  const [showAccounts, setShowAccounts] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    setAccounts(data?.accounts);
    setShowAccounts(!data?.showInvalidMicrosoftAccountWarning);
  }, [data]);

  const updateAccountList = (accountId: string, isPersonal: boolean, name: string) => {
    if (data) {
      setAccounts([
        ...data.accounts.filter((account) => !account.isExternal),
        {
          accountId,
          accountDisplayId: accountId,
          isPersonal,
          name,
          isExternal: true,
          verified: true
        },
        ...data.accounts.filter((account) => account.isExternal)
      ]);
    }
  };

  useEffect(() => {
    if (translations.applicationTitle && translations.chooseAccountTitle && translations.companyName) {
      document.title = `${translations.chooseAccountTitle} - ${translations.applicationTitle} - ${translations.companyName}`;
    }
  }, [translations]);

  const setSelectedAccount = async (accountId: string, isExternal: boolean) => {
    const response = await selectAccount(accountId, isExternal);
    if (response.isVerified) {
      returnToPreviousUrl();
    } else {
      setInitialStep(Steps.AddAccount);

      !showAddProfileModal && setShowAddProfileModal(true);
    }
  };

  return (
    <Page inputs={[data]} errors={[error]} showMenu={false} errorMessage={translations.brregUnavailableErrorMessage}>
      {showAccounts && (
        <Section className="choose-account">
          {disablePersonal && !disableSearch && (
            <Notification type="generic" fullWidth>
              {translations.personalAccountsNotAllowed}
            </Notification>
          )}
          {disablePersonal && disableSearch && (
            <Notification type="generic" fullWidth>
              {translations.personalAndExternalAccountsNotAllowed}
            </Notification>
          )}
          {!disablePersonal && disableSearch && (
            <Notification type="generic" fullWidth>
              {translations.externalAccountsNotAllowed}
            </Notification>
          )}
          <LayoutGrid>
            <h1 className="choose-account__heading">{translations.chooseAccountTitle}</h1>
            <div className="choose-account__accounts">
              <div className="choose-account__new-account">
                {!disableSearch && !data?.natRegInformationFailed && (
                  <Button
                    className="choose-account__new-account-button"
                    type="button"
                    variant={ButtonVariant.Solid}
                    theme={Theme.Neutral}
                    onClick={() => setShowAddProfileModal(true)}
                    innerRef={addCompanyRef}
                  >
                    {translations.chooseAccountAddCompanyLabel}
                    <FaIcon name={FaIcons.Plus} className="choose-account__icon" />
                  </Button>
                )}

                <AddProfileModal
                  showModal={showAddProfileModal}
                  disablePersonal={disablePersonal}
                  disableSearch={disableSearch}
                  initialStep={initialStep}
                  updateActiveStep={setInitialStep}
                  onClose={() => {
                    addCompanyRef.current?.focus();
                    setShowAddProfileModal(false);
                  }}
                  goToPreviousUrl={returnToPreviousUrl}
                  updateAccountList={updateAccountList}
                  personalAccount={accounts?.find((a) => a.isPersonal)}
                />
              </div>
              <p className="choose-account__choose-profile">{translations.chooseProfileText}</p>

              {!disablePersonal && !data?.natRegInformationFailed && !data?.accounts.some((a) => a.isPersonal) && (
                <Notification type="generic" fullWidth>
                  {translations.couldNotGetPersonalAccountErrorMessage}
                </Notification>
              )}
              {data?.natRegInformationFailed && (
                <Notification type="error" fullWidth>
                  {translations.couldNotFetchFromNationalRegisterError}
                </Notification>
              )}
              {data?.showNoAccountWarning && (
                <Notification type="generic" fullWidth>
                  {translations.noAccountErrorMessage}
                </Notification>
              )}
              {data?.showInvalidMicrosoftAccountWarning && (
                <Notification type="generic" fullWidth>
                  {translations.invalidMicrosoftAccountErrorMessage}
                </Notification>
              )}

              {accounts && accounts.length > 0 && !data?.showInvalidMicrosoftAccountWarning && (
                <ul>
                  {accounts.filter((account) => account.isPersonal).length > 0 &&
                    accounts
                      .filter((account) => account.isPersonal)
                      .map((e) => (
                        <li key={e.accountId}>
                          <AccountCard
                            {...e}
                            displayId={e.accountDisplayId}
                            onSelectAccount={setSelectedAccount}
                            isPersonal={e.isPersonal}
                            personal={translations.chooseAccountPersonalText}
                          />
                        </li>
                      ))}
                  {accounts.length > 0 &&
                    accounts
                      .filter((account) => !account.isPersonal)
                      .map((e) => (
                        <li key={e.accountId}>
                          <AccountCard
                            {...e}
                            displayId={e.accountDisplayId}
                            onSelectAccount={setSelectedAccount}
                            isPersonal={e.isPersonal}
                            company={translations.chooseAccountCompanyText}
                          />
                        </li>
                      ))}
                </ul>
              )}
            </div>
          </LayoutGrid>
        </Section>
      )}
      {!showAccounts && (
        <Section className="choose-account">
          {data?.showInvalidMicrosoftAccountWarning && (
            <div>
              <Notification type="generic" fullWidth>
                <p
                  dangerouslySetInnerHTML={{
                    __html: translations.invalidMicrosoftAccountErrorMessage
                  }}
                />
              </Notification>
              <Button
                className="choose-account__logout-button"
                type="button"
                variant={ButtonVariant.Solid}
                theme={Theme.Neutral}
                onClick={() => navigate("/single-sign-out")}
              >
                {layout?.logOutLabel}
              </Button>
            </div>
          )}
        </Section>
      )}
    </Page>
  );
};

export default ChooseAccount;
